import React from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
  SxProps,
  Theme,
} from "@mui/material";
import { CiSearch } from "react-icons/ci";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { B13, B13Ink, B13TypeScale, tint12 } from "../../theme/b13Tokens";

// Reusable MUI-styled searchable Select. Closed state renders exactly
// like a plain MUI <Select> so it lines up with sibling TextField
// filters in the same row; open state adds a sticky autofocused
// search input at the top of the menu that filters options in memory.
//
// Caller passes `sx` so the trigger matches whichever filter-field
// primitive the surrounding page already uses (e.g. Course List's
// filterFieldSx or Student List's slightly taller variant). This is
// what keeps four filters in a row visually balanced.

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  allLabel: string;
  searchPlaceholder: string;
  noResultsText: string;
  ariaLabel: string;
  loading?: boolean;
  loadingText?: string;
  /** Optional floating label. When provided, the closed state
   *  matches a sibling MUI <TextField select label={…}> visually. */
  label?: string;
  sx?: SxProps<Theme>;
}

const SearchableSelect: React.FC<Props> = ({
  value,
  onChange,
  options,
  allLabel,
  searchPlaceholder,
  noResultsText,
  ariaLabel,
  loading,
  loadingText,
  label,
  sx,
}) => {
  const labelId = React.useId();
  const [search, setSearch] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);

  const displayed = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
    // Always keep the selected option renderable so MUI can resolve
    // its MenuItem (avoids "out-of-range value" warning).
    if (value && !filtered.some((o) => o.value === value)) {
      const selectedOpt = options.find((o) => o.value === value);
      if (selectedOpt) return [selectedOpt, ...filtered];
    }
    return filtered;
  }, [options, search, value]);

  return (
    <FormControl fullWidth size="small" sx={sx}>
      {label && (
        <InputLabel id={labelId} shrink>
          {label}
        </InputLabel>
      )}
      <Select
        displayEmpty
        notched={Boolean(label)}
        labelId={label ? labelId : undefined}
        input={label ? <OutlinedInput label={label} notched /> : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value as string)}
        onClose={() => setSearch("")}
        aria-label={label ? undefined : ariaLabel}
        renderValue={(v) => {
          const val = String(v ?? "");
          if (!val) return allLabel;
          const found = options.find((o) => o.value === val);
          return found ? found.label : val;
        }}
        MenuProps={{
          autoFocus: false,
          TransitionProps: {
            onEntered: () => {
              searchInputRef.current?.focus();
            },
          },
          PaperProps: {
            sx: {
              mt: 0.5,
              maxHeight: 420,
              borderRadius: "12px",
              boxShadow: "0 10px 32px rgba(15,23,42,0.14)",
              border: `1px solid ${B13.line}`,
              overflow: "hidden",
            },
          },
          MenuListProps: {
            autoFocusItem: false,
            sx: { pt: 0 },
          },
        }}
      >
        <ListSubheader
          sx={{
            p: 1,
            top: 0,
            bgcolor: "#FFFFFF",
            borderBottom: `1px solid ${B13.line}`,
            lineHeight: 1,
          }}
        >
          <TextField
            inputRef={searchInputRef}
            size="small"
            fullWidth
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              const passthrough = new Set([
                "ArrowDown",
                "ArrowUp",
                "Enter",
                "Escape",
                "Tab",
              ]);
              if (!passthrough.has(e.key)) {
                e.stopPropagation();
              }
            }}
            onClick={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CiSearch size={16} color={B13Ink.ink3} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => {
                      setSearch("");
                      searchInputRef.current?.focus();
                    }}
                    sx={{
                      color: B13Ink.ink3,
                      "&:hover": { color: B13.primary, bgcolor: tint12(B13.primary) },
                    }}
                  >
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                minHeight: 40,
                borderRadius: "10px",
                bgcolor: "#F8FAFC",
                "& fieldset": { borderColor: B13.line },
                "&:hover fieldset": { borderColor: tint12(B13.primary) },
                "&.Mui-focused fieldset": { borderColor: B13.primary, borderWidth: "1px" },
                "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(31,111,235,0.15)" },
              },
              "& .MuiOutlinedInput-input": { fontSize: 13.5, color: B13.ink, py: 0.85 },
            }}
          />
        </ListSubheader>

        <MenuItem value="">{allLabel}</MenuItem>

        {loading ? (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ ...B13TypeScale.caption, color: B13Ink.ink3 }}>
              {loadingText || "Loading…"}
            </Typography>
          </Box>
        ) : displayed.length === 0 ? (
          <Box sx={{ px: 2, py: 1.5, textAlign: "center" }}>
            <Typography sx={{ ...B13TypeScale.caption, color: B13Ink.ink3 }}>
              {noResultsText}
            </Typography>
          </Box>
        ) : (
          displayed.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
};

export default SearchableSelect;
